"use client";

import { motion } from "framer-motion";
import {
  Activity,
  CalendarClock,
  Car,
  ChevronDown,
  DollarSign,
  MapPin,
  ShoppingBag,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { AppShellMock } from "./AppShellMock";

const LOJAS = [
  { nome: "Filial SP", est: "43", fat: "R$ 284k", ativa: true },
  { nome: "Filial RJ", est: "28", fat: "R$ 162k", ativa: false },
  { nome: "Filial BH", est: "19", fat: "R$ 98k", ativa: false },
] as const;

const KPI_ROW = [
  { label: "Garagem", value: "90", icon: Car, color: "text-primary", bg: "bg-primary/10" },
  { label: "Vendas (R$)", value: "R$ 544k", icon: DollarSign, color: "text-emerald-500", bg: "bg-emerald-500/10" },
  { label: "Vendas (Qtd)", value: "18", icon: ShoppingBag, color: "text-purple-500", bg: "bg-purple-500/10" },
  { label: "Ticket Médio", value: "R$ 30k", icon: Activity, color: "text-blue-500", bg: "bg-blue-500/10" },
  { label: "Agendam.", value: "12", icon: CalendarClock, color: "text-orange-500", bg: "bg-orange-500/10" },
] as const;

const RANKING = [
  { n: "Carlos Mendes", l: "SP · BH", v: "12" },
  { n: "Ana Ribeiro", l: "RJ", v: "9" },
  { n: "Lucas Prado", l: "SP", v: "8" },
] as const;

const CHART = [28, 42, 38, 55, 48, 62, 58, 72, 65, 80] as const;

export default function MockRedeShowcase() {
  return (
    <div
      className="pointer-events-none flex h-full min-h-[200px] select-none flex-col"
      aria-label="Prévia fictícia da visão consolidada da rede (Dashboard)"
    >
      <AppShellMock highlight="dashboard">
        <div className="h-full min-h-0 overflow-y-auto overflow-x-hidden bg-background p-2 text-foreground md:p-3">
          <div className="mb-3 flex flex-col justify-between gap-2 md:flex-row md:items-end">
            <div>
              <h1 className="text-sm font-bold tracking-tight text-foreground md:text-base">
                Visão da rede
              </h1>
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                Consolidação multi-loja — mesmo padrão do painel principal.
              </p>
            </div>
            <span className="inline-flex h-8 items-center gap-1.5 rounded-2xl border-2 border-primary px-2 text-[9px] font-bold text-foreground shadow-md shadow-primary/20">
              Período: Mês
              <ChevronDown className="h-3 w-3 opacity-70" aria-hidden />
            </span>
          </div>

          <div className="mb-3 grid grid-cols-3 gap-1 md:gap-2">
            {LOJAS.map((loja) => (
              <div
                key={loja.nome}
                className={cn(
                  "rounded-xl border p-1.5 md:rounded-2xl md:p-2",
                  loja.ativa
                    ? "border-primary/40 bg-primary/5"
                    : "border-border bg-card"
                )}
              >
                <div className="flex items-center gap-0.5 text-[8px] font-semibold text-foreground">
                  <MapPin className="h-2.5 w-2.5 text-muted-foreground" aria-hidden />
                  {loja.nome}
                </div>
                <p className="mt-0.5 text-[8px] text-muted-foreground">
                  <span className="font-bold text-foreground">{loja.est}</span> veíc.
                </p>
                <p className="text-[10px] font-black tabular-nums text-foreground">{loja.fat}</p>
              </div>
            ))}
          </div>

          <section className="mb-3 grid grid-cols-2 gap-1.5 md:grid-cols-5">
            {KPI_ROW.map((k) => (
              <Card
                key={k.label}
                className="flex flex-col items-start gap-1.5 rounded-2xl border border-border bg-card p-2 shadow-sm md:gap-2 md:rounded-3xl md:p-3"
              >
                <div className={cn("rounded-lg p-1.5 md:rounded-xl md:p-2.5", k.bg)}>
                  <k.icon className={cn("h-3.5 w-3.5 md:h-5 md:w-5", k.color)} aria-hidden />
                </div>
                <div>
                  <h3 className="text-xs font-bold tracking-tight text-foreground md:text-xl">{k.value}</h3>
                  <p className="text-[8px] font-medium text-muted-foreground md:text-xs">{k.label}</p>
                </div>
              </Card>
            ))}
          </section>

          <div className="mb-2 flex items-start gap-1.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-2 py-1.5">
            <span className="text-[9px] font-semibold text-amber-700 dark:text-amber-200">
              4 veículos com mais de 30 dias no pátio — revisar giro entre filiais.
            </span>
          </div>

          <div className="rounded-2xl border border-border bg-card p-2 shadow-sm">
            <p className="mb-2 text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">
              Performance (rede)
            </p>
            <div className="mb-2 flex h-14 items-end gap-0.5 md:h-16">
              {CHART.map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{ delay: i * 0.04, duration: 0.35, ease: "easeOut" }}
                  className={cn("min-w-[3px] flex-1 rounded-t-sm", i === 8 ? "bg-primary" : "bg-primary/25")}
                  aria-hidden
                />
              ))}
            </div>
            <div className="border-t border-border pt-2">
              <p className="mb-1 text-[8px] font-semibold uppercase text-muted-foreground">
                Ranking vendedores
              </p>
              <div className="grid grid-cols-[1fr_auto_auto] gap-x-2 border-b border-border/50 pb-0.5 text-[8px] font-semibold text-muted-foreground">
                <span>Nome</span>
                <span className="text-center">Loja</span>
                <span className="text-right">Vendas</span>
              </div>
              <ul className="divide-y divide-border/40">
                {RANKING.map((r, i) => (
                  <li key={r.n} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-2 py-1 text-[9px]">
                    <span className="flex items-center gap-1 min-w-0">
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[8px] font-bold">
                        {i + 1}
                      </span>
                      <span className="truncate font-medium">{r.n}</span>
                    </span>
                    <span className="text-center text-muted-foreground">{r.l}</span>
                    <span className="text-right font-bold text-primary">{r.v}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-2 text-[8px] text-muted-foreground">Interface de demonstração — dados fictícios.</p>
        </div>
      </AppShellMock>
    </div>
  );
}
